import { KIWebhookRequest, KIWebhookResponse, LektoratIntensity } from '../types';

export class KIService {
  // Der produktive n8n-Webhook und sein Zugang liegen serverseitig in Infisical.
  // Der Browser spricht ausschließlich den Same-Origin-Proxy an.
  private static proxyUrl = '/api/ki/lektorat';

  // KI-Lektorat anfordern
  static async requestLektorat(
    content: string,
    intensity: LektoratIntensity,
    options?: {
      topic?: string;
      title?: string;
      subtitle?: string;
      categoryId?: string;
      dossierId?: string;
      teaser?: string;
      lektoratHinweise?: string[];
    }
  ): Promise<KIWebhookResponse> {
    try {
      const request: KIWebhookRequest = {
        content,
        intensity,
        topic: options?.topic,
        title: options?.title,
        subtitle: options?.subtitle,
        categoryId: options?.categoryId,
        dossierId: options?.dossierId,
        teaser: options?.teaser,
        lektoratHinweise: options?.lektoratHinweise,
        // Kompatibilitätsfelder für den bestehenden n8n-Workflow; kein Browser-Secret mehr.
        signature: '',
        timestamp: Date.now(),
      };

      const jwt = await this.createSessionJwt();
      const response = await fetch(this.proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Appwrite-JWT': jwt,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: KIWebhookResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'KI-Lektorat fehlgeschlagen');
      }

      return result;
    } catch (error) {
      throw new Error('KI-Lektorat konnte nicht durchgeführt werden');
    }
  }

  // Bestehendes Lektorat-Payloadformat über den geschützten Same-Origin-Proxy senden.
  // Die KI-API-Felder der Plattform enthalten keine aufrufbare Ziel-URL und kein Browser-Secret mehr.
  // Das getrennte apiEndpoint-Feld für die eigentliche Artikelpublikation bleibt davon unberührt.
  static async submitLektoratPayload(payload: unknown, signal?: AbortSignal): Promise<Response> {
    const jwt = await this.createSessionJwt();
    return fetch('/api/ki/lektorat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Appwrite-JWT': jwt,
      },
      body: JSON.stringify(payload),
      signal,
    });
  }

  // KI-Bildgenerierung und Metadaten verwenden getrennte Plattform-Workflows.
  // Sie bleiben in diesem ersten Migrationsschritt unverändert.

  private static async createSessionJwt(): Promise<string> {
    const { account } = await import('../config/appwrite');
    const session = await account.createJWT();
    return session.jwt;
  }

  // Lektorat-Intensität validieren
  static validateIntensity(intensity: string): intensity is LektoratIntensity {
    return ['rechtschreibung', 'leicht', 'tiefgreifend'].includes(intensity);
  }

  // Bildvalidierung (16:9 Format, Mindestauflösung)
  static validateImage(imageFile: File): Promise<{
    isValid: boolean;
    errors: string[];
  }> {
    return new Promise((resolve) => {
      const errors: string[] = [];
      
      // Dateigröße prüfen (max 30MB)
      if (imageFile.size > 30 * 1024 * 1024) {
        errors.push('Bilddatei ist zu groß (max. 30MB)');
      }

      // Dateityp prüfen
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(imageFile.type)) {
        errors.push('Nur JPEG, PNG und WebP Dateien sind erlaubt');
      }

      // Bilddimensionen prüfen
      const img = new Image();
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        const targetRatio = 16 / 9;
        const tolerance = 0.1; // 10% Toleranz

        if (Math.abs(aspectRatio - targetRatio) > tolerance) {
          errors.push('Bild sollte im 16:9 Format sein');
        }

        if (img.width < 1200 || img.height < 675) {
          errors.push('Mindestauflösung: 1200x675 Pixel');
        }

        resolve({
          isValid: errors.length === 0,
          errors,
        });
      };

      img.onerror = () => {
        errors.push('Bild konnte nicht geladen werden');
        resolve({
          isValid: false,
          errors,
        });
      };

      img.src = URL.createObjectURL(imageFile);
    });
  }
}
